import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { camerasApi } from '@/api/endpoints'
import { toast } from '@/lib/toast'
import * as Switch from '@radix-ui/react-switch'
import { cn } from '@/lib/utils'
import type { Camera } from '@/types'

/**
 * CP 189 — superuser-only switch pinning a camera's status to Online.
 *
 * Renders nothing unless the viewer is a superuser AND the camera is HTTP:
 * FTP cameras report `is_online: null` (no heartbeat protocol) and have no
 * status to pin, so the server rejects them with 400.
 *
 * Saves on toggle rather than on the sheet's Save button, because the mode is
 * written through its own superuser-gated endpoint, not the generic camera
 * PATCH that the rest of the sheet submits.
 */
export function OnlineStatusModeRow({
  camera,
  isSuperuser,
}: {
  camera: Camera
  isSuperuser: boolean
}) {
  const { t } = useTranslation('cameras')
  const queryClient = useQueryClient()

  const setMode = useMutation({
    mutationFn: (mode: 'auto' | 'manual') => camerasApi.setOnlineStatusMode(camera.id, mode),
    onSuccess: (_data, mode) => {
      // The pill is rendered from is_online on the camera queries, so they have
      // to refetch for it to flip.
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      // AllCamerasPage keys its rows ['all-cameras'] and re-fetches the sheet's
      // camera as ['camera', id]; ['cameras'] does not prefix-match either, and
      // the global staleTime is 30s, so both are invalidated by name.
      queryClient.invalidateQueries({ queryKey: ['all-cameras'] })
      queryClient.invalidateQueries({ queryKey: ['camera', camera.id] })
      toast.success(
        mode === 'manual'
          ? t('edit.onlineStatusModeManual')
          : t('edit.onlineStatusModeAuto'),
      )
    },
    onError: () => toast.error(t('edit.onlineStatusModeFailed')),
  })

  if (!isSuperuser || camera.ingestion_type !== 'http') return null

  // `camera` is a snapshot: CamerasPage holds the sheet's camera in useState and
  // never re-syncs it from the refetched list while the sheet is open, so this
  // prop still says 'auto' after a successful write. Prefer what is being
  // written, then what the server confirmed, and fall back to the camera only
  // before the first write and after a failed one.
  const written = setMode.isPending ? setMode.variables : setMode.data?.online_status_mode
  const mode = written ?? camera.online_status_mode

  const pinned = mode === 'manual'

  // Not SwitchRow: the client asked for a switch that names both modes
  // ("Status (Manual <> Auto)"), so the row carries a word on each side with the
  // active one emphasised. Row and switch styling stay identical to SwitchRow.
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-border/50 last:border-0">
      <div className="flex-1 min-w-0">
        <label htmlFor="sw-online-status-mode" className="text-sm font-medium cursor-pointer">
          {t('edit.onlineStatusMode')}
        </label>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t('edit.onlineStatusModeHint')}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={cn('text-xs', pinned ? 'text-muted-foreground' : 'font-medium text-foreground')}>
          {t('edit.onlineStatusAuto')}
        </span>
        <Switch.Root
          id="sw-online-status-mode"
          checked={pinned}
          onCheckedChange={(v) => setMode.mutate(v ? 'manual' : 'auto')}
          disabled={setMode.isPending}
          className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=unchecked]:bg-input disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Switch.Thumb className="pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
        </Switch.Root>
        <span className={cn('text-xs', pinned ? 'font-medium text-foreground' : 'text-muted-foreground')}>
          {t('edit.onlineStatusManual')}
        </span>
      </div>
    </div>
  )
}
